import React, { useState } from 'react';
import { Modal, Radio, Space } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import Button, { Type as ButtonType } from 'src/components/button';
import FitFilters from 'src/pages/reports/fit/filters';
import FitHistoryTable from 'src/pages/reports/fit/table';
import './style.scss';

/**
 * Fits History page component.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.data - Fit History records.
 * @param {Object} props.pagination - Pagination metadata.
 * @param {Object} props.filterOptions - Filter options for dropdowns.
 * @param {boolean} props.loading - Whether the table is loading.
 * @param {boolean} props.exporting - Whether export is in progress.
 * @param {Function} props.onFilterChange - Callback for filter changes.
 * @param {Function} props.onPaginationChange - Callback for pagination changes.
 * @param {Function} props.onExport - Callback for export action.
 * @returns {JSX.Element} The FitHistoryPage component.
 */
function FitHistoryPage({
  data,
  pagination,
  filterOptions,
  loading,
  exporting,
  onFilterChange,
  onPaginationChange,
  onSortChange,
  onExport,
}) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('csv');

  const handleExport = () => {
    onExport(selectedFormat);
    setModalVisible(false);
  };

  return (
    <div className='reports-fit'>
      <div className='reports-fit__header'>
        <h2 className='reports-fit__header_counter'>
          {pagination.totalCount} {t('reports.recordsFound')}
        </h2>
        <Button
          type={ButtonType.Primary}
          className='btn-export'
          icon={exporting ? <LoadingOutlined /> : <DownloadOutlined />}
          uppercase
          disabled={exporting}
          onClick={() => setModalVisible(true)}
        >
          {exporting ? t('reports.exporting') : t('reports.exportResults')}
        </Button>
      </div>
      <FitFilters onApiFilter={onFilterChange} filterOptions={filterOptions} />
      <FitHistoryTable
        data={data}
        pagination={pagination}
        loading={loading}
        onPaginationChange={onPaginationChange}
        onSortChange={onSortChange}
      />
      <Modal
        title={t('reports.exportResultsAs')}
        visible={modalVisible}
        wrapClassName='reports-export-modal'
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key='cancel' onClick={() => setModalVisible(false)}>{t('cancel')}</Button>,
          <Button key='export' type={ButtonType.Primary} className='btn-export' onClick={handleExport}>{t('export')}</Button>,
        ]}
      >
        <Radio.Group value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)}>
          <Space direction='vertical'>
            <Radio value='csv'>.CSV</Radio>
            <Radio value='xlsx'>.XLSX</Radio>
            <Radio value='pdf'>.PDF</Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
}

FitHistoryPage.defaultProps = {
  data: [],
  pagination: { currentPage: 1, totalCount: 0, limit: 10 },
  filterOptions: {},
  loading: false,
  exporting: false,
  onFilterChange: noop,
  onPaginationChange: noop,
  onSortChange: noop,
  onExport: noop,
};

export default FitHistoryPage;
